import { useEffect, useState, useCallback } from "react";
import Model, { Providers } from "@tripian/model";
import { providers } from "@tripian/core";

interface PriorityProducts {
  viator: Providers.Viator.Product[];
  gyg: Providers.Gyg.Tour[];
  toristy: Providers.Toristy.Product[];
  victory: Providers.Victory.Event[];
  bookbarbados: Providers.Bb.Product[];
  rezdy: Providers.Rezdy.Product[];
}

const initialPriorityProducts: PriorityProducts = {
  viator: [],
  gyg: [],
  toristy: [],
  victory: [],
  bookbarbados: [],
  rezdy: [],
};

const usePriorityTours = () => {
  const [priorityProducts, setPriorityProducts] = useState<PriorityProducts>(
    initialPriorityProducts
  );
  const [loading, setLoading] = useState(false);

  const fetchViatorPriorityTours = useCallback(
    async (productCodes: string[]): Promise<Providers.Viator.Product[]> => {
      if (!providers.viator) return [];

      const products: Providers.Viator.Product[] = [];

      for (const productCode of productCodes) {
        try {
          const tourData = await providers.viator.tourData(productCode);
          if (tourData && tourData.info) {
            // Convert TourData to Product format for catalog display
            // Using partial structure that works with useBookableCatalogs
            const product = {
              productCode: tourData.availability?.productCode || productCode,
              title: tourData.info.title,
              description: tourData.info.description,
              pricing: {
                summary: {
                  fromPrice: tourData.availability?.summary?.fromPrice || 0,
                  fromPriceBeforeDiscount: undefined,
                },
                currency: tourData.availability?.currency || "USD",
              },
              images: tourData.info.images || [],
              reviews: tourData.info.reviews,
              duration: tourData.info.itinerary?.duration,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              tags: (tourData.info.tags as any)?.map(
                (t: { tagId: number } | number) =>
                  typeof t === "number" ? t : t.tagId
              ) || [],
              flags: [],
              translationInfo: {
                containsMachineTranslatedText: false,
              },
              productUrl: tourData.info.productUrl,
            } as unknown as Providers.Viator.Product;

            products.push(product);
          }
        } catch (error) {
          console.warn(
            `Failed to fetch priority Viator product: ${productCode}`,
            error
          );
          // Silently skip failed products
        }
      }

      return products;
    },
    []
  );

  const fetchGygPriorityTours = useCallback(
    async (tourIds: number[]): Promise<Providers.Gyg.Tour[]> => {
      if (!providers.gyg) return [];

      const tours: Providers.Gyg.Tour[] = [];

      for (const tourId of tourIds) {
        try {
          const tour = await providers.gyg.tour(tourId);
          if (tour) {
            tours.push(tour);
          }
        } catch (error) {
          console.warn(`Failed to fetch priority GYG tour: ${tourId}`, error);
        }
      }

      return tours;
    },
    []
  );

  useEffect(() => {
    const priorityConfig = window.tconfig.priorityTourIds;

    if (!priorityConfig) {
      return;
    }

    const fetchAllPriorityTours = async () => {
      setLoading(true);

      const newPriorityProducts: PriorityProducts = { ...initialPriorityProducts };

      // Fetch Viator priority tours
      const hasViatorIds = priorityConfig.viator && priorityConfig.viator.length > 0;
      const viatorEnabled = window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(Model.PROVIDER_ID.VIATOR);

      if (hasViatorIds && viatorEnabled && priorityConfig.viator) {
        newPriorityProducts.viator = await fetchViatorPriorityTours(
          priorityConfig.viator
        );
      }

      // Fetch GYG priority tours
      if (
        priorityConfig.gyg &&
        priorityConfig.gyg.length > 0 &&
        window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(Model.PROVIDER_ID.GYG)
      ) {
        newPriorityProducts.gyg = await fetchGygPriorityTours(priorityConfig.gyg);
      }

      // TODO: Add other providers as needed (toristy, victory, bookbarbados, rezdy)

      setPriorityProducts(newPriorityProducts);
      setLoading(false);
    };

    fetchAllPriorityTours();
  }, [fetchViatorPriorityTours, fetchGygPriorityTours]);

  return {
    priorityProducts,
    loadingPriorityTours: loading,
  };
};

export default usePriorityTours;